import React, { useEffect, useState } from "react";
import { ErrorMessage, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import axios from "../../../../../axios-instance";
import Loader from "../../../../../components/Loader/Loader";
import { getUserBusinesses } from "../../../../../utils/authHelper";
import { notification } from "../../../../../utils/utility";
import OnboardingSetupLayout, {
  OnboardingActions,
  OnboardingFieldError,
} from "../../../../../components/OnboardingSetup/OnboardingSetupLayout";
import {
  getStepTwoPath,
  isOtherEventType,
  isOtherProfession,
} from "../../../../../components/OnboardingSetup/onboardingSteps";

const getSelectedInterestsFromStorage = () => {
  try {
    const savedInterests = localStorage.getItem("selectedInterests");
    if (savedInterests) {
      return JSON.parse(savedInterests).map((id) => String(id));
    }
  } catch (error) {
    // ignore invalid storage
  }
  return [];
};

const ProfileStepThreeView = () => {
  const navigate = useNavigate();
  const [pageLoading, setPageLoading] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [profession, setProfession] = useState([]);
  const [initialEventTypes, setInitialEventTypes] = useState(
    getSelectedInterestsFromStorage()
  );

  useEffect(() => {
    axios
      .get("/profession-types")
      .then((response) => {
        setProfession(response.data.data.profession_types || []);
      })
      .catch(() => {
        notification.error("Could not load roles. Refresh and try again.");
      });
  }, []);

  useEffect(() => {
    setPageLoading(true);
    axios
      .get("/event-types")
      .then((response) => {
        setPageLoading(false);
        setEventTypes(response.data.data.eventTypes || []);
      })
      .catch(() => {
        setPageLoading(false);
        notification.error("Could not load event types. Refresh and try again.");
      });

    return () => {
      localStorage.removeItem("selectedInterests");
    };
  }, []);

  useEffect(() => {
    if (eventTypes.length === 0) {
      return;
    }
    const savedInterests = getSelectedInterestsFromStorage();
    const validEventTypeIds = eventTypes.map((item) => String(item.id));
    const validSelected = savedInterests.filter((id) =>
      validEventTypeIds.includes(id)
    );
    if (validSelected.length > 0) {
      setInitialEventTypes(validSelected);
    }
  }, [eventTypes]);

  const hasOtherEventInList = eventTypes.some(isOtherEventType);

  return (
    <>
      <Loader isShowLoader={pageLoading} />
      <Formik
        initialValues={{
          role: "",
          other_role: "",
          event_types: initialEventTypes,
          other_event_type: "",
        }}
        enableReinitialize
        validationSchema={Yup.object().shape({
          role: Yup.string().required(
            "Select the option that best describes your role."
          ),
          event_types: Yup.array()
            .of(Yup.string())
            .min(1, "Select at least one event type."),
        })}
        onSubmit={(values) => {
          const selectedProfession = profession.find(
            (item) => String(item.id) === String(values.role)
          );
          const payload = { ...values };
          if (isOtherProfession(selectedProfession)) {
            if (!values.other_role.trim()) {
              notification.error("Please specify your role.");
              return;
            }
            payload.role = values.other_role.trim();
          }

          let eventTypeIds = [...values.event_types];
          const otherSelected = eventTypeIds.some((id) => {
            const match = eventTypes.find((item) => String(item.id) === String(id));
            return String(id) === "9" || isOtherEventType(match);
          });
          if (otherSelected) {
            if (!values.other_event_type.trim()) {
              notification.error("Please specify the event type.");
              return;
            }
            eventTypeIds = eventTypeIds.filter((id) => String(id) !== "9");
            eventTypeIds.push(values.other_event_type.trim());
          }

          setPageLoading(true);
          if (getUserBusinesses()) {
            payload.business_id = Object.values(getUserBusinesses()).pop();
          }
          axios
            .post("/admin/quick-setup-step-three", {
              ...payload,
              event_types: eventTypeIds.join(","),
            })
            .then(() => {
              setPageLoading(false);
              navigate("/signup/profile-step-four");
            })
            .catch((error) => {
              setPageLoading(false);
              const message =
                error.response?.data?.message ||
                "Could not save preferences. Please try again.";
              notification.error(message);
            });
        }}
      >
        {(formik) => {
          const { values, handleChange, handleSubmit, handleBlur } = formik;
          const selectedProfession = profession.find(
            (item) => String(item.id) === String(values.role)
          );
          const showOtherRole = isOtherProfession(selectedProfession);
          const showOtherEvent =
            values.event_types.includes("9") ||
            values.event_types.some((id) => {
              const match = eventTypes.find((item) => String(item.id) === String(id));
              return isOtherEventType(match);
            });

          return (
            <OnboardingSetupLayout
              currentStep={3}
              title="How you use MeetOcto"
              description="This only helps us tailor examples — it does not limit what you can create."
            >
              <form className="onboarding-setup__form" onSubmit={handleSubmit} noValidate>
                <div className="onboarding-setup__panels">
                  <section className="onboarding-setup__panel">
                    <h2 className="onboarding-setup__panel-title">Your role</h2>
                    <div
                      className="onboarding-setup__choices"
                      role="radiogroup"
                      aria-labelledby="role_label"
                    >
                      <span className="visually-hidden" id="role_label">
                        Your role
                      </span>
                      {profession.map((item) => (
                        <label className="onboarding-setup__choice" key={item.id}>
                          <input
                            type="radio"
                            name="role"
                            value={item.id}
                            checked={String(values.role) === String(item.id)}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <span>{item.profession}</span>
                        </label>
                      ))}
                    </div>
                    <ErrorMessage name="role" component={OnboardingFieldError} />
                    {showOtherRole ? (
                      <div className="onboarding-setup__field">
                        <label className="onboarding-setup__label" htmlFor="other_role">
                          Specify your role
                        </label>
                        <input
                          type="text"
                          className="onboarding-setup__input"
                          name="other_role"
                          id="other_role"
                          placeholder="Your role"
                          value={values.other_role}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </div>
                    ) : null}
                  </section>

                  <section className="onboarding-setup__panel">
                    <h2 className="onboarding-setup__panel-title">Event types</h2>
                    <div
                      className="onboarding-setup__choices"
                      role="group"
                      aria-labelledby="events_label"
                    >
                      <span className="visually-hidden" id="events_label">
                        Main event types
                      </span>
                      {eventTypes.map((item) => (
                        <label className="onboarding-setup__choice" key={item.id}>
                          <input
                            type="checkbox"
                            name="event_types"
                            value={item.id}
                            checked={values.event_types.includes(String(item.id))}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <span>{item.event_type}</span>
                        </label>
                      ))}
                      {!hasOtherEventInList ? (
                        <label className="onboarding-setup__choice">
                          <input
                            type="checkbox"
                            name="event_types"
                            value="9"
                            checked={values.event_types.includes("9")}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <span>Other</span>
                        </label>
                      ) : null}
                    </div>
                    <ErrorMessage name="event_types" component={OnboardingFieldError} />
                    {showOtherEvent ? (
                      <div className="onboarding-setup__field">
                        <label
                          className="onboarding-setup__label"
                          htmlFor="other_event_type"
                        >
                          Specify event type
                        </label>
                        <input
                          type="text"
                          className="onboarding-setup__input"
                          name="other_event_type"
                          id="other_event_type"
                          placeholder="Event type"
                          value={values.other_event_type}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </div>
                    ) : null}
                  </section>
                </div>

                <OnboardingActions
                  onBack={() => navigate(getStepTwoPath())}
                  submitLabel="Continue"
                />
              </form>
            </OnboardingSetupLayout>
          );
        }}
      </Formik>
    </>
  );
};

export default ProfileStepThreeView;
